
import { computed, ref, type Ref } from 'vue';
import { nanoid } from 'nanoid';
import { isValid, uploadFileToEndpoint } from './helper';
export const UploadStates = {
  'uploading': 'uploading',
  'pending': 'pending',
  'failed': 'failed',
  'success': 'success',
} as const;

type ObjectValues<T> = T[keyof T];
type FileUploadStates = ObjectValues<typeof UploadStates>;

export type FileToUpload = {
  uuid: string;
  progress: number;
  file: File;
  state: FileUploadStates;
}

export type FileOptions = {
  maxFilesize: number;
  acceptedFiles: string[];
}

const files: Ref<Map<string, Ref<FileToUpload>>> = ref(new Map());
export function useUploadManager(apiUrl: string, token: string[], options: FileOptions,
  mockUploadFunction: boolean) {
  // clone of tokens
  const uploadToken = [...token]

  const getFiles = computed(() => [...files.value.values()])
  const getValidFiles = computed(() => [...files.value.values()].filter((file) => isValid(file, options)))
  const fileCount = computed(() => files.value.size)
  const validfilesCount = computed(() => getFiles.value.filter((file) => isValid(file, options)).length)
  const successfulUploadCount = computed(() => getFiles.value.filter((file) => file.value.state === 'success').length)

  const status = computed(() => {
    const states = getValidFiles.value.map((file) => file.value.state);
    const hasIsUploading = states.includes('uploading');
    const hasSuccess = states.includes('success');
    const hasIsPending = states.includes('pending');
    const hasErrors = states.includes('failed');

    if (hasSuccess && !hasIsUploading && !hasIsPending && !hasErrors) return 'success';
    if (hasErrors && !hasSuccess && !hasIsUploading && !hasIsPending) return 'failed';
    if (hasIsUploading) return 'uploading';
    if (hasSuccess && !hasIsUploading && !hasIsPending && hasErrors) return 'partialsuccess';
    return 'pending';
  })

  // add files to the upload manager
  // if we have more files than tokens / maxFiles, we only add as many files as we have tokens
  // in case of livestream token we have as many tokens as maxFiles
  const addFiles = (addedFiles: File[]) => {
    addedFiles.forEach((file) => {
      const f = ref({
        uuid: nanoid(),
        progress: 0,
        file: file,
        state: UploadStates.pending,
      })
      files.value.set(f.value.uuid, f);
    })
  }

  const removeFile = (uuid: string) => files.value.delete(uuid);
  const clearFiles = () => files.value.clear();

  const uploadAllFiles = () => {
    if (files.value.size === 0) return;
    const validFiles = getFiles.value.filter((file) => isValid(file, options));
    if (validFiles.length > token.length || uploadToken.length < 1) {
      console.error('Not enough tokens to upload all files');
      return;
    }

    // initiate upload of all files
    // async is fine here even though it's a forEach
    // because we are not waiting for the result of the upload as preconition
    // for the next upload
    validFiles.forEach(async (value) => {
      // token can't be empty at this point
      const tkn = token.pop() as string;
      if (!tkn) throw new Error('Token is empty - this should be impossible');
      const uploadSuccess = await uploadFileToEndpoint(
        apiUrl,
        value,
        tkn,
        mockUploadFunction);
      if (!uploadSuccess) {
        // if the upload failed, we need to put the token back
        token.push(tkn);
      }
    });
  }

  const uploadSingleFile = async (uuid: string) => {
    if (files.value.size === 0) return;
    if (uploadToken.length < 1) {
      console.error('Not enough tokens to upload all files');
      return;
    }

    const tkn = token.pop() as string;
    if (!tkn) throw new Error('Token is empty - this should be impossible');

    const file = files.value.get(uuid);
    if (!file) {
      console.error('File not found');
      return;
    }

    const uploadSuccess = await uploadFileToEndpoint(
      apiUrl,
      file,
      tkn,
      mockUploadFunction);
    if (!uploadSuccess) {
      // if the upload failed, we need to put the token back
      token.push(tkn);
    }
  }

  return {
    // functions
    addFiles,
    removeFile,
    clearFiles,
    uploadAllFiles,
    uploadSingleFile,
    // computed values
    files: getFiles,
    status,
    fileCount,
    successfulUploadCount,
    uploadToken,
    validfilesCount,
    getFilesMap: files.value,
  }
}
